/**
 * Forensic audit export — Johan-only investigative tool.
 * v4.7.47 — converts the structured payload returned by
 * /api/admin/full-financial-audit into XLSX (multi-sheet workbook) or
 * PDF (multi-table A4 landscape) entirely client-side. Server stores
 * nothing; binaries are streamed straight to disk via the browser.
 * v4.7.52 — adds downloadClientStatementPdf() — a clean, branded, UUID-free
 * PDF designed to be sent to the client. No breakpoints, no diagnostics,
 * no internal codes; counterparty by name + email only. Empty sections
 * auto-hide. Running balance walks forward from 0 at user signup.
 *
 * Strict read-side: no mutation of audit data, no upload, no transmit.
 */
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { buildAccountLedger } from './accountLedger';
import { buildStatementShareTotals } from './statementShareTotals';

const fmt = (n: any, d = 2) => (typeof n === 'number' ? n : parseFloat(n ?? 0) || 0).toFixed(d);
const dt = (s: any) => (s ? new Date(s).toLocaleString() : '');
const cp = (u: any) => u ? `${u.username || ''} <${u.email || ''}> #${u.id ?? ''}`.trim() : '';

function ts() {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
}

function fileBase(data: any) {
  const u = data?.profile || {};
  const safeName = String(u.username || 'user').replace(/[^a-zA-Z0-9_.-]/g, '_');
  return `aureus-audit-${safeName}-${u.id ?? 'x'}-${ts()}`;
}

function sheet(rows: any[], wb: XLSX.WorkBook, name: string) {
  if (!rows || rows.length === 0) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['(no rows)']]), name.slice(0, 31));
    return;
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), name.slice(0, 31));
}

export function buildAuditWorkbook(data: any): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  const profile = data?.profile || {};
  const kyc = data?.kyc || {};
  const recon = data?.reconciliation || {};

  sheet([{ field: 'target_user_id', value: data?.target_user_id }, { field: 'generated_at', value: data?.generated_at }, { field: 'username', value: profile.username }, { field: 'email', value: profile.email }, { field: 'full_name', value: profile.full_name }, { field: 'country', value: profile.country_name || profile.country_of_residence }, { field: 'account_status', value: profile.account_status }, { field: 'created_at', value: profile.created_at }, { field: 'last_login_at', value: profile.last_login_at }, { field: 'kyc_status', value: kyc.kyc_status }, { field: 'kyc_full_legal_name', value: kyc.full_legal_name }, { field: 'kyc_completed_at', value: kyc.kyc_completed_at }, { field: 'forensic_disclaimer', value: data?.disclaimer }], wb, 'Profile');

  sheet(data?.share_purchases?.all || [], wb, 'Share Purchases');
  sheet(data?.crypto_payments?.rows || [], wb, 'Crypto Payments');
  sheet(data?.yoco_payments?.rows || [], wb, 'Yoco Payments');

  const ce = data?.commissions_earned || {};
  const ceRows = [
    ...(ce.commission_transactions || []).map((c: any) => ({ source_table: 'commission_transactions', id: c.id, from_user: cp(c.counterparty_user), referred_id: c.referred_id, share_purchase_id: c.share_purchase_id, level: '', commission_rate: c.commission_rate, share_purchase_amount: c.share_purchase_amount, usdt_commission: c.usdt_commission, share_commission: c.share_commission, transaction_type: c.transaction_type, status: c.status, phase_id: c.phase_id, created_at: c.created_at })),
    ...(ce.multi_level_commissions || []).map((c: any) => ({ source_table: 'multi_level_commissions', id: c.id, from_user: cp(c.counterparty_user), referred_id: c.referred_id, share_purchase_id: c.original_purchase_id, level: c.level, commission_rate: '', share_purchase_amount: c.share_purchase_amount, usdt_commission: c.usdt_commission, share_commission: '', transaction_type: c.commission_type, status: c.status, phase_id: c.phase_id, created_at: c.created_at }))
  ];
  sheet(ceRows, wb, 'Commissions Earned');

  const cpo = data?.commissions_paid_to_others || {};
  const cpoRows = [
    ...(cpo.commission_transactions || []).map((c: any) => ({ source_table: 'commission_transactions', id: c.id, to_user: cp(c.counterparty_user), referrer_id: c.referrer_id, share_purchase_id: c.share_purchase_id, level: '', commission_rate: c.commission_rate, share_purchase_amount: c.share_purchase_amount, usdt_commission: c.usdt_commission, share_commission: c.share_commission, transaction_type: c.transaction_type, status: c.status, phase_id: c.phase_id, created_at: c.created_at })),
    ...(cpo.multi_level_commissions || []).map((c: any) => ({ source_table: 'multi_level_commissions', id: c.id, to_user: cp(c.counterparty_user), referrer_id: c.referrer_id, share_purchase_id: c.original_purchase_id, level: c.level, commission_rate: '', share_purchase_amount: c.share_purchase_amount, usdt_commission: c.usdt_commission, share_commission: '', transaction_type: c.commission_type, status: c.status, phase_id: c.phase_id, created_at: c.created_at }))
  ];
  sheet(cpoRows, wb, 'Commissions Paid Out');

  sheet(data?.commission_balance ? [data.commission_balance] : [], wb, 'Commission Balance');
  sheet(data?.commission_conversions?.rows || [], wb, 'Conversions');
  sheet(data?.withdrawals?.commission_withdrawal_requests || [], wb, 'CW Requests');
  sheet(data?.withdrawals?.commission_withdrawals || [], wb, 'CW Withdrawals');
  sheet(data?.withdrawals?.withdrawal_requests || [], wb, 'W Requests');
  sheet(data?.withdrawals?.withdrawal_transactions || [], wb, 'W Transactions');
  sheet(data?.share_transfers?.sent || [], wb, 'Transfers Sent');
  sheet(data?.share_transfers?.received || [], wb, 'Transfers Received');
  sheet(data?.competition_prizes?.rows || [], wb, 'Prizes');
  sheet(data?.admin_audit_logs || [], wb, 'Admin Audit Logs');

  const reconRows = Object.entries({ has_upline: recon.has_upline, recorded_total_earned_usdt: recon.recorded_total_earned_usdt, gross_usdt_all_commission_records: recon.gross_usdt_all_commission_records, net_usdt_earned_excluding_rejected_cancelled_reversed_failed: recon.net_usdt_earned_excluding_rejected_cancelled_reversed_failed, usdt_earned_diff_recorded_minus_net: recon.usdt_earned_diff_recorded_minus_net, gross_usdt_all_paid_to_others_records: recon.gross_usdt_all_paid_to_others_records, net_usdt_paid_to_others_excluding_rejected_cancelled_reversed_failed: recon.net_usdt_paid_to_others_excluding_rejected_cancelled_reversed_failed, counterparty_user_index_size: recon.counterparty_user_index_size, breakpoint_count: recon.breakpoint_count }).map(([field, value]) => ({ field, value }));
  sheet(reconRows, wb, 'Reconciliation');
  sheet(recon.breakpoints || [], wb, 'Breakpoints');

  return wb;
}

export function downloadAuditXlsx(data: any) {
  const wb = buildAuditWorkbook(data);
  XLSX.writeFile(wb, `${fileBase(data)}.xlsx`);
}

function pdfTable(doc: jsPDF, title: string, head: string[][], body: any[][]) {
  if (body.length === 0) return;
  autoTable(doc, { head, body, startY: (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 8 : 60, styles: { fontSize: 7, cellPadding: 2 }, headStyles: { fillColor: [245, 158, 11], textColor: 0 }, margin: { left: 20, right: 20 }, didDrawPage: (d: any) => { doc.setFontSize(9); doc.text(title, 20, d.cursor.y - 4 < 50 ? 50 : 50); } });
}

export function buildAuditPdf(data: any): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const profile = data?.profile || {};
  doc.setFontSize(16); doc.text('Aureus Africa — Full Financial Audit (Forensic)', 20, 30);
  doc.setFontSize(9);
  doc.text(`User: ${profile.username || ''} <${profile.email || ''}> #${profile.id ?? ''} · Generated ${dt(data?.generated_at)}`, 20, 46);

  const ce = data?.commissions_earned || {};
  pdfTable(doc, 'Commissions Earned', [['ID', 'From (counterparty)', 'Purchase', 'Level', 'USDT', 'Status', 'Created']], [
    ...(ce.commission_transactions || []).map((c: any) => [String(c.id ?? ''), cp(c.counterparty_user), String(c.share_purchase_id ?? ''), '-', `$${fmt(c.usdt_commission)}`, c.status || '', dt(c.created_at)]),
    ...(ce.multi_level_commissions || []).map((c: any) => [String(c.id ?? ''), cp(c.counterparty_user), String(c.original_purchase_id ?? ''), String(c.level ?? ''), `$${fmt(c.usdt_commission)}`, c.status || '', dt(c.created_at)])
  ]);

  const cpo = data?.commissions_paid_to_others || {};
  pdfTable(doc, 'Commissions Paid To Others', [['ID', 'To (upline)', 'Purchase', 'Level', 'USDT', 'Status', 'Created']], [
    ...(cpo.commission_transactions || []).map((c: any) => [String(c.id ?? ''), cp(c.counterparty_user), String(c.share_purchase_id ?? ''), '-', `$${fmt(c.usdt_commission)}`, c.status || '', dt(c.created_at)]),
    ...(cpo.multi_level_commissions || []).map((c: any) => [String(c.id ?? ''), cp(c.counterparty_user), String(c.original_purchase_id ?? ''), String(c.level ?? ''), `$${fmt(c.usdt_commission)}`, c.status || '', dt(c.created_at)])
  ]);

  pdfTable(doc, 'Share Purchases', [['ID', 'Package', 'Shares', 'Amount', 'Method', 'Status', 'Topup', 'Created']],
    (data?.share_purchases?.all || []).map((p: any) => [String(p.id ?? ''), p.package_name || '', fmt(p.shares_purchased, 4), `$${fmt(p.total_amount)}`, p.payment_method || '', p.status || '', p.is_commission_topup ? 'Y' : '', dt(p.created_at)]));

  pdfTable(doc, 'Crypto Payments', [['ID', 'Amount', 'Currency', 'Network', 'Status', 'Created']],
    (data?.crypto_payments?.rows || []).map((r: any) => [String(r.id ?? ''), fmt(r.amount), r.currency || '', r.network || '', r.status || '', dt(r.created_at)]));

  pdfTable(doc, 'Yoco Payments (ZAR cents)', [['ID', 'Total', 'Method', 'Status', 'Webhook', 'Reference', 'Created']],
    (data?.yoco_payments?.rows || []).map((r: any) => [String(r.id ?? ''), String(r.total_amount ?? ''), r.payment_method || '', r.status || '', r.webhook_processed ? 'Y' : 'N', r.reference_number || '', dt(r.created_at)]));

  pdfTable(doc, 'Conversions', [['ID', 'Shares Req', 'USDT', 'Status', 'Created']],
    (data?.commission_conversions?.rows || []).map((r: any) => [String(r.id ?? ''), fmt(r.shares_requested, 4), `$${fmt(r.usdt_amount)}`, r.status || '', dt(r.created_at)]));

  const w = data?.withdrawals || {};
  pdfTable(doc, 'Commission Withdrawal Requests', [['ID', 'Amount', 'Currency', 'Status', 'Created']],
    (w.commission_withdrawal_requests || []).map((r: any) => [String(r.id ?? ''), `$${fmt(r.withdrawal_amount)}`, r.currency || '', r.status || '', dt(r.created_at)]));

  pdfTable(doc, 'Commission Withdrawals', [['ID', 'Type', 'Net', 'Status', 'Completed']],
    (w.commission_withdrawals || []).map((r: any) => [String(r.id ?? ''), r.withdrawal_type || '', `$${fmt(r.net_amount)}`, r.status || '', dt(r.completed_at)]));

  pdfTable(doc, 'Share Transfers Sent', [['ID', 'Recipient', 'Shares', 'Status', 'Created']],
    (data?.share_transfers?.sent || []).map((t: any) => [String(t.id ?? ''), String(t.recipient_user_id ?? ''), fmt(t.shares_transferred, 4), t.status || '', dt(t.created_at)]));

  pdfTable(doc, 'Share Transfers Received', [['ID', 'Sender', 'Shares', 'Status', 'Created']],
    (data?.share_transfers?.received || []).map((t: any) => [String(t.id ?? ''), String(t.sender_user_id ?? ''), fmt(t.shares_transferred, 4), t.status || '', dt(t.created_at)]));

  const recon = data?.reconciliation || {};
  pdfTable(doc, 'Reconciliation Breakpoints', [['Severity', 'Category', 'Reference', 'Amount', 'When', 'Message']],
    (recon.breakpoints || []).map((b: any) => [b.severity || '', b.category || '', String(b.purchase_id || b.withdrawal_id || b.transfer_id || b.row_id || ''), b.amount != null ? `$${fmt(b.amount)}` : b.diff_usdt != null ? `Δ $${fmt(b.diff_usdt)}` : '', dt(b.created_at), String(b.message || '')]));

  return doc;
}

export function downloadAuditPdf(data: any) {
  const doc = buildAuditPdf(data);
  doc.save(`${fileBase(data)}.pdf`);
}


// ─── Client Statement PDF (v4.7.52) ────────────────────────────────────────
// A clean, branded PDF intended to be forwarded to the client. No UUIDs, no
// diagnostic codes, no breakpoints. Counterparty by name + email only. Sections
// with zero records are omitted automatically.

const TERMINAL_CS = {
  share_purchase: new Set(['approved', 'active', 'consolidated', 'completed', 'paid']),
  commission: new Set(['paid', 'completed', 'approved']),
  conversion: new Set(['approved']),
  withdrawal: new Set(['paid', 'completed']),
  transfer: new Set(['completed']),
};
const normCs = (s: any) => String(s ?? '').trim().toLowerCase();
const cpName = (u: any, fallbackLabel = '(unknown user)') => {
  if (!u) return fallbackLabel;
  const name = u.username || u.full_name || '';
  return `${name}${u.email ? ` <${u.email}>` : ''}`.trim() || fallbackLabel;
};
const signedCs = (n: number, d = 2) => (n > 0 ? '+' : '') + fmt(n, d);
const KIND_LABEL_CS: Record<string, string> = {
  share_purchase: 'Purchase', commission_shares: 'Comm. shares', commission_topup: 'Top-up',
  commission_earned: 'Earned', commission_conversion: 'Convert', withdrawal: 'Withdraw',
  transfer_sent: 'Transfer out', transfer_received: 'Transfer in', legacy_shares: 'Legacy', reconciliation: 'Adjustment',
};

function clientTable(doc: jsPDF, title: string, head: string[][], body: any[][]) {
  if (!body.length) return;
  const y = (doc as any).lastAutoTable ? (doc as any).lastAutoTable.finalY + 12 : 130;
  doc.setFontSize(11); doc.setTextColor(245, 158, 11); doc.text(title, 36, y - 4);
  doc.setTextColor(0, 0, 0);
  autoTable(doc, {
    head, body, startY: y,
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [245, 158, 11], textColor: 0, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    margin: { left: 36, right: 36 },
  });
}

export function buildClientStatementPdf(data: any): jsPDF {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const profile = data?.profile || {};
  const signupAt = profile.created_at;
  const L = buildAccountLedger(data);
  const ledger = L.rows;
  const closingUsdt = L.closingUsdt;
  const closingShares = L.closingShares;
  const wholeShares = Math.floor(closingShares);
  const fracShares = +(closingShares - wholeShares).toFixed(4);

  // ── Header ────────────────────────────────────────────────────────────
  doc.setFillColor(15, 23, 42);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 70, 'F');
  doc.setTextColor(245, 158, 11); doc.setFontSize(20);
  doc.text('AUREUS AFRICA', 36, 32);
  doc.setTextColor(229, 231, 235); doc.setFontSize(11);
  doc.text('Client Account Statement', 36, 52);
  doc.setFontSize(9); doc.setTextColor(156, 163, 175);
  doc.text(`Generated: ${dt(data?.generated_at || new Date().toISOString())}`, doc.internal.pageSize.getWidth() - 36, 32, { align: 'right' });
  doc.setTextColor(0, 0, 0);

  // ── Client info block ────────────────────────────────────────────────
  const fullName = profile.full_name || profile.username || '—';
  const email = profile.email || '—';
  const country = profile.country_name || profile.country_of_residence || '—';
  doc.setFontSize(10);
  doc.text(`Account holder: ${fullName}`, 36, 92);
  doc.text(`Email: ${email}`, 36, 106);
  doc.text(`Country: ${country}`, 36, 120);
  doc.text(`Account opened: ${dt(signupAt)}`, 400, 92);
  doc.text(`Opening balance: USDT $0.00 · Shares 0.0000`, 400, 106);
  doc.text(`Closing balance: USDT $${fmt(closingUsdt)} · Shares ${fmt(closingShares, 4)}  (${wholeShares} whole + ${fmt(fracShares, 4)} fraction)`, 400, 120);
  if (L.escrowUsdt > 0 || L.escrowShares > 0) {
    doc.setTextColor(180, 83, 9);
    doc.text(`Held in escrow (not available): USDT $${fmt(L.escrowUsdt)}${L.escrowShares > 0 ? ` · Shares ${fmt(L.escrowShares, 4)}` : ''}`, 400, 134);
    doc.setTextColor(0, 0, 0);
  }

  // ── Share totals (purchased / commission / bonus) ────────────────────
  const totals = buildStatementShareTotals(data);
  const totalsStartY = (L.escrowUsdt > 0 || L.escrowShares > 0) ? 156 : 142;
  const totalsRows: any[][] = [
    ['Purchased', fmt(totals.purchased, 4), 'Shares you paid for'],
    ['Commission', fmt(totals.commission, 4), 'Commission, pool, and ACRES shares already credited as yours'],
    ['Bonus', fmt(totals.bonus, 4), 'Gift and bonus shares already credited as yours'],
  ];
  if (Math.abs(totals.other) >= 0.0001) {
    totalsRows.push(['Other owned', fmt(totals.other, 4), 'Transfers, conversions, or legacy shares not in the three groups above']);
  }
  totalsRows.push(['Total owned', fmt(totals.totalOwned, 4), 'Same as closing shares']);
  doc.setFontSize(11); doc.setTextColor(245, 158, 11); doc.text('Share totals — what you own', 36, totalsStartY - 4);
  doc.setTextColor(0, 0, 0);
  autoTable(doc, {
    head: [['Group', 'Shares', 'Meaning']],
    body: totalsRows,
    startY: totalsStartY,
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [245, 158, 11], textColor: 0, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 250, 252] },
    columnStyles: { 0: { cellWidth: 110 }, 1: { cellWidth: 80, halign: 'right' } },
    margin: { left: 36, right: 36 },
  });
  if (totals.walletUnconverted > 0) {
    const wy = (doc as any).lastAutoTable.finalY + 14;
    doc.setFontSize(9); doc.setTextColor(55, 65, 81);
    doc.text(`Commission wallet (not owned yet): ${fmt(totals.walletUnconverted, 4)} shares still sit in your commission wallet. They are not included in Total owned.`, 36, wy, { maxWidth: 760 });
    doc.setTextColor(0, 0, 0);
    (doc as any).lastAutoTable.finalY = wy + 6;
  }

  // ── Chronological statement ──────────────────────────────────────────
  if (ledger.length === 0) {
    const emptyY = ((doc as any).lastAutoTable?.finalY || 160) + 20;
    doc.setFontSize(11); doc.text('No financial movement recorded on this account.', 36, emptyY);
  } else {
    clientTable(doc,
      'Statement — Chronological Ledger',
      [['Date', 'Type', 'Description', 'Counterparty', 'Status', 'USDT Δ', 'Shares Δ', 'USDT Balance', 'Shares Balance']],
      ledger.map(r => [
        dt(r.when), KIND_LABEL_CS[r.kind] || r.kind, r.label,
        r.counterparty ? cpName(r.counterparty, '—') : '—',
        r.terminal ? (r.status || '') : `${r.status} (informational)`,
        r.terminal ? `$${signedCs(r.usdt_delta)}` : '—',
        r.terminal ? signedCs(r.shares_delta, 4) : '—',
        `$${fmt(r.usdt_balance || 0)}`,
        fmt(r.shares_balance || 0, 4),
      ])
    );
  }

  // ── Detail sections (auto-hide when empty) ───────────────────────────
  // Holdings breakdown must tie to the closing shares balance: count only
  // status='active' non-topup purchases (mirrors canonicalBalances). Folded
  // 'consolidated' source rows already appear as informational rows in the
  // chronological ledger above, so purchase history is not lost.
  const purchases = (data?.share_purchases?.all || []).filter((p: any) => !p.is_commission_topup && normCs(p.status) === 'active');
  if (purchases.length) clientTable(doc, 'Share Purchases',
    [['Date', 'Package', 'Shares', 'Bonus', 'Amount', 'Method', 'Status']],
    purchases.map((p: any) => [dt(p.created_at), p.package_name || '—', fmt(p.shares_purchased, 4), fmt(p.bonus_shares, 4), `$${fmt(p.total_amount)}`, p.payment_method || '—', p.status || ''])
  );

  const conversions = (data?.commission_conversions?.rows || []).filter((r: any) => normCs(r.status) === 'approved');
  if (conversions.length) clientTable(doc, 'Commission → Share Conversions',
    [['Date', 'Shares', 'USDT spent', 'Status']],
    conversions.map((r: any) => [dt(r.approved_at || r.created_at), fmt(r.shares_requested, 4), `$${fmt(r.usdt_amount)}`, r.status || ''])
  );

  const txSent = (data?.share_transfers?.sent || []).filter((t: any) => normCs(t.status) === 'completed');
  const txRecv = (data?.share_transfers?.received || []).filter((t: any) => normCs(t.status) === 'completed');
  if (txSent.length) clientTable(doc, 'Share Transfers Sent',
    [['Date', 'Recipient', 'Shares', 'Reason']],
    txSent.map((t: any) => [dt(t.completed_at || t.created_at), cpName(t.counterparty_user, 'recipient'), fmt(t.shares_transferred, 4), t.transfer_reason || '—'])
  );
  if (txRecv.length) clientTable(doc, 'Share Transfers Received',
    [['Date', 'Sender', 'Shares', 'Reason']],
    txRecv.map((t: any) => [dt(t.completed_at || t.created_at), cpName(t.counterparty_user, 'sender'), fmt(t.shares_transferred, 4), t.transfer_reason || '—'])
  );

  const ce = data?.commissions_earned || {};
  const ceRows = [
    ...(ce.commission_transactions || []).filter((c: any) => TERMINAL_CS.commission.has(normCs(c.status))).map((c: any) => ['direct', c]),
    ...(ce.multi_level_commissions || []).filter((c: any) => TERMINAL_CS.commission.has(normCs(c.status))).map((c: any) => ['mlc', c]),
  ];
  if (ceRows.length) clientTable(doc, 'Commissions Earned (from your referrals)',
    [['Date', 'From (referred user)', 'Level', 'USDT earned', 'Shares', 'Status']],
    ceRows.map(([kind, c]: any) => [dt(c.created_at), cpName(c.counterparty_user, 'referred user'), kind === 'mlc' ? String(c.level) : 'direct', `$${fmt(c.usdt_commission)}`, fmt(c.share_commission || c.shares_purchased, 4), c.status || ''])
  );

  const cpo = data?.commissions_paid_to_others || {};
  const cpoRows = [
    ...(cpo.commission_transactions || []).filter((c: any) => TERMINAL_CS.commission.has(normCs(c.status))).map((c: any) => ['direct', c]),
    ...(cpo.multi_level_commissions || []).filter((c: any) => TERMINAL_CS.commission.has(normCs(c.status))).map((c: any) => ['mlc', c]),
  ];
  if (cpoRows.length) clientTable(doc, 'Commissions Generated (paid to your upline from your purchases)',
    [['Date', 'To (upline)', 'Level', 'USDT paid', 'Status']],
    cpoRows.map(([kind, c]: any) => [dt(c.created_at), cpName(c.counterparty_user, 'upline'), kind === 'mlc' ? String(c.level) : 'direct', `$${fmt(c.usdt_commission)}`, c.status || ''])
  );

  if (data?.referrer) clientTable(doc, 'Referrer',
    [['Referrer', 'Referral code used', 'Linked at']],
    [[cpName(data.referrer.user, 'referrer'), data.referrer.referral_code || '—', dt(data.referrer.created_at)]]
  );

  // ── Footer on every page ─────────────────────────────────────────────
  const pageCount = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8); doc.setTextColor(107, 114, 128);
    doc.text(`Aureus Africa — Confidential client statement. Generated ${dt(data?.generated_at || new Date().toISOString())}. Page ${i} of ${pageCount}.`,
      doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 18, { align: 'center' });
  }
  return doc;
}

export function downloadClientStatementPdf(data: any) {
  const doc = buildClientStatementPdf(data);
  const u = data?.profile || {};
  const safe = String(u.username || u.full_name || 'client').replace(/[^a-zA-Z0-9_.-]/g, '_');
  doc.save(`aureus-statement-${safe}-${ts()}.pdf`);
}
